import React from 'react';
import { sortByPoints } from '../gameUtils';

const COLS = ['PTS', 'REB', 'AST', 'STL', 'BLK', 'TO', 'FG', '3P', 'FT', '+/-'];
const cell = { padding: '8px 4px', textAlign: 'center' };
const mono = { ...cell, fontFamily: 'monospace' };
const totalCell = { padding: '10px 4px', textAlign: 'center' };
const totalMono = { ...totalCell, fontFamily: 'monospace' };

const pmColor = (pm) => (pm > 0 ? '#34d399' : pm < 0 ? '#fb7185' : 'rgba(255,255,255,0.5)');

function LandscapeTeam({ team, rows, t }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, color: '#fbbf24' }}>{team.name.toUpperCase()}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.08em' }}>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>#</th>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Player</th>
            {COLS.map(c => <th key={c} style={{ textAlign: 'center', padding: '6px 4px' }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {sortByPoints(rows).map(p => {
            const pm = p.plus_minus || 0;
            return (
              <tr key={p.player_id || p.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '8px 4px', fontFamily: 'monospace', color: 'rgba(255,255,255,0.5)' }}>{p.jersey || '-'}</td>
                <td style={{ padding: '8px 4px', fontWeight: 500 }}>{p.name}</td>
                <td style={{ ...cell, fontWeight: 700 }}>{p.pts || 0}</td>
                <td style={cell}>{p.reb || 0}</td>
                <td style={cell}>{p.ast || 0}</td>
                <td style={cell}>{p.stl || 0}</td>
                <td style={cell}>{p.blk || 0}</td>
                <td style={cell}>{p.to || 0}</td>
                <td style={mono}>{p.fgm || 0}/{p.fga || 0}</td>
                <td style={mono}>{p.three_m || 0}/{p.three_a || 0}</td>
                <td style={mono}>{p.ftm || 0}/{p.fta || 0}</td>
                <td style={{ ...cell, color: pmColor(pm) }}>{pm > 0 ? '+' : ''}{pm}</td>
              </tr>
            );
          })}
          <tr style={{ borderTop: '2px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.03)', fontWeight: 700 }}>
            <td colSpan={2} style={{ padding: '10px 4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 12 }}>Team Totals</td>
            <td style={totalCell}>{t.pts}</td>
            <td style={totalCell}>{t.reb}</td>
            <td style={totalCell}>{t.ast}</td>
            <td style={totalCell}>{t.stl || 0}</td>
            <td style={totalCell}>{t.blk || 0}</td>
            <td style={totalCell}>{t.to || 0}</td>
            <td style={totalMono}>{t.fgm}/{t.fga}</td>
            <td style={totalMono}>{t.three_m}/{t.three_a}</td>
            <td style={totalMono}>{t.ftm}/{t.fta}</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LandscapeScore({ name, score, align }) {
  return (
    <div style={{ textAlign: align }}>
      <div style={{ fontSize: 14, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>{name}</div>
      <div style={{ fontSize: 72, fontWeight: 900, lineHeight: 1 }}>{score}</div>
    </div>
  );
}

// Off-screen 1600px-wide render (teams side by side) used for shared/downloaded PNGs.
export const BoxScoreLandscape = React.forwardRef(function BoxScoreLandscape(
  { game, boxscore, teamTotals, scoreA, scoreB, period }, ref,
) {
  return (
    <div
      ref={ref}
      data-testid="boxscore-landscape-capture"
      style={{
        position: 'fixed', left: '-99999px', top: 0, width: 1600,
        background: '#0A0F2C', color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif', padding: '32px 40px',
      }}
      aria-hidden="true"
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 32, paddingBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <LandscapeScore name={game.team_a.name} score={scoreA} align="right" />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, letterSpacing: '0.3em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 6 }}>Live Box Score</div>
          <div style={{ display: 'inline-block', background: '#fbbf24', color: '#000', fontSize: 16, fontWeight: 900, padding: '6px 12px', borderRadius: 6 }}>{period}</div>
        </div>
        <LandscapeScore name={game.team_b.name} score={scoreB} align="left" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, paddingTop: 24 }}>
        {['team_a', 'team_b'].map(tk => (
          <LandscapeTeam key={tk} team={game[tk]} rows={boxscore[tk]} t={teamTotals[tk]} />
        ))}
      </div>
      <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>
        via Schedule Guru
      </div>
    </div>
  );
});
